import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/apiClient';
import { Event, Invitation, UserProfile } from '../types';

const EventOrganizersPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [event, setEvent] = useState<Event | null>(null);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);

  const loadData = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load event details
      const eventResponse = await apiClient.get(`/v1/events/${eventId}`);
      setEvent(eventResponse.data.data);

      // Load invitations
      const invitationsResponse = await apiClient.get(`/v1/events/${eventId}/invitations`);
      setInvitations(invitationsResponse.data.data || []);
    } catch (err: any) {
      console.error('Failed to load data:', err);
      setError(err.response?.data?.error?.message || 'Failed to load organizer data');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleInvitationSent = () => {
    setShowInviteModal(false);
    loadData();
  };

  const handleRemoveCoOrganizer = async (userId: string) => {
    if (!window.confirm('Are you sure you want to remove this co-organizer?')) {
      return;
    }

    try {
      await apiClient.delete(`/v1/events/${eventId}/organizers/${userId}`);
      loadData();
    } catch (err: any) {
      console.error('Failed to remove co-organizer:', err);
      alert(err.response?.data?.error?.message || 'Failed to remove co-organizer');
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    if (!window.confirm('Are you sure you want to cancel this invitation?')) {
      return;
    }

    try {
      await apiClient.delete(`/v1/invitations/${invitationId}`);
      loadData();
    } catch (err: any) {
      console.error('Failed to cancel invitation:', err);
      alert(err.response?.data?.error?.message || 'Failed to cancel invitation');
    }
  };

  const isCreator = event && user && event.creator_id === user.sub;
  const isCoOrganizer = event && user && event.co_organizers?.includes(user.sub);
  const canManage = isCreator || isCoOrganizer;

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading organizers...</p>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error || 'Event not found'}
        </div>
        <button
          className="btn btn-secondary"
          onClick={() => navigate('/events')}
        >
          <i className="fas fa-arrow-left me-2"></i>
          Back to Events
        </button>
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          You don't have permission to manage organizers for this event.
        </div>
        <button
          className="btn btn-secondary"
          onClick={() => navigate(`/events/${eventId}`)}
        >
          <i className="fas fa-arrow-left me-2"></i>
          Back to Event
        </button>
      </div>
    );
  }

  const pendingInvitations = invitations.filter(inv => inv.status === 'pending');

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-lg-10 offset-lg-1">
          <button
            className="btn btn-link text-decoration-none mb-3"
            onClick={() => navigate(`/events/${eventId}`)}
          >
            <i className="fas fa-arrow-left me-2"></i>
            Back to Event
          </button>

          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1>
                <i className="fas fa-users me-2"></i>
                Manage Organizers
              </h1>
              <p className="text-muted mb-0">{event.name}</p>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => setShowInviteModal(true)}
            >
              <i className="fas fa-user-plus me-2"></i>
              Invite Co-Organizer
            </button>
          </div>

          {/* Creator */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-crown me-2 text-warning"></i>
                Event Creator
              </h5>
            </div>
            <div className="card-body">
              <div className="d-flex align-items-center">
                <i className="fas fa-user-circle fa-3x text-primary me-3"></i>
                <div>
                  <h6 className="mb-0">{event.creator_name || 'Unknown'}</h6>
                  <small className="text-muted">Event Creator</small>
                </div>
              </div>
            </div>
          </div>

          {/* Co-Organizers */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-users me-2"></i>
                Co-Organizers ({event.co_organizer_details?.length || 0})
              </h5>
            </div>
            <div className="card-body">
              {event.co_organizer_details && event.co_organizer_details.length > 0 ? (
                <div className="list-group list-group-flush">
                  {event.co_organizer_details.map((coOrg) => (
                    <div key={coOrg.user_id} className="list-group-item d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        {coOrg.profile_image_url ? (
                          <img
                            src={coOrg.profile_image_url}
                            alt={coOrg.name}
                            className="rounded-circle me-3"
                            style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                          />
                        ) : (
                          <i className="fas fa-user-circle fa-3x text-secondary me-3"></i>
                        )}
                        <div>
                          <h6 className="mb-0">{coOrg.name}</h6>
                          <small className="text-muted">{coOrg.email}</small>
                        </div>
                      </div>
                      {isCreator && (
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleRemoveCoOrganizer(coOrg.user_id)}
                        >
                          <i className="fas fa-times me-1"></i>
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  <i className="fas fa-users-slash fa-3x mb-3"></i>
                  <p>No co-organizers yet.</p>
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => setShowInviteModal(true)}
                  >
                    <i className="fas fa-user-plus me-2"></i>
                    Invite Co-Organizer
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Pending Invitations */}
          {pendingInvitations.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="fas fa-clock me-2"></i>
                  Pending Invitations ({pendingInvitations.length})
                </h5>
              </div>
              <div className="card-body">
                <div className="list-group list-group-flush">
                  {pendingInvitations.map((invitation) => (
                    <div key={invitation.invitation_id} className="list-group-item d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="mb-0">{invitation.invitee_name || 'Unknown User'}</h6>
                        <small className="text-muted">
                          Invited {new Date(invitation.created_at).toLocaleDateString()}
                        </small>
                      </div>
                      <div>
                        <span className="badge bg-warning me-2">Pending</span>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => handleCancelInvitation(invitation.invitation_id)}
                        >
                          <i className="fas fa-times me-1"></i>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <InviteCoOrganizerModal
          eventId={eventId!}
          eventName={event.name}
          onClose={() => setShowInviteModal(false)}
          onSuccess={handleInvitationSent}
        />
      )}
    </div>
  );
};

interface InviteCoOrganizerModalProps {
  eventId: string;
  eventName: string;
  onClose: () => void;
  onSuccess: () => void;
}

const InviteCoOrganizerModal: React.FC<InviteCoOrganizerModalProps> = ({
  eventId,
  eventName,
  onClose,
  onSuccess
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserProfile[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      return;
    }

    try {
      setSearching(true);
      setError(null);
      const response = await apiClient.get('/v1/users/search', {
        params: { query: searchQuery }
      });
      setSearchResults(response.data.data || []);
    } catch (err: any) {
      console.error('Search failed:', err);
      setError(err.response?.data?.error?.message || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleInvite = async () => {
    if (!selectedUser) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await apiClient.post(`/v1/events/${eventId}/invite-organizer`, {
        invitee_id: selectedUser.user_id
      });

      onSuccess();
    } catch (err: any) {
      console.error('Failed to send invitation:', err);
      setError(err.response?.data?.error?.message || 'Failed to send invitation');
      setSubmitting(false);
    }
  };

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fas fa-user-plus me-2"></i>
              Invite Co-Organizer
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={submitting}
            ></button>
          </div>

          <div className="modal-body">
            {error && (
              <div className="alert alert-danger">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {error}
              </div>
            )}

            <div className="mb-3">
              <p className="text-muted">
                Invite a user to be a co-organizer for <strong>{eventName}</strong>
              </p>
            </div>

            {!selectedUser ? (
              <>
                <div className="mb-3">
                  <label htmlFor="search" className="form-label">
                    Search for User
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      id="search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      placeholder="Search by name or email..."
                      disabled={searching}
                    />
                    <button
                      className="btn btn-primary"
                      onClick={handleSearch}
                      disabled={searching || !searchQuery.trim()}
                    >
                      {searching ? (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      ) : (
                        <i className="fas fa-search"></i>
                      )}
                    </button>
                  </div>
                </div>

                {searchResults.length > 0 && (
                  <div className="list-group">
                    {searchResults.map((user) => (
                      <button
                        key={user.user_id}
                        className="list-group-item list-group-item-action d-flex align-items-center"
                        onClick={() => setSelectedUser(user)}
                      >
                        {user.profile_image_url ? (
                          <img
                            src={user.profile_image_url}
                            alt={user.name}
                            className="rounded-circle me-3"
                            style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                          />
                        ) : (
                          <i className="fas fa-user-circle fa-2x text-secondary me-3"></i>
                        )}
                        <div>
                          <h6 className="mb-0">{user.name}</h6>
                          <small className="text-muted">{user.email}</small>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      {selectedUser.profile_image_url ? (
                        <img
                          src={selectedUser.profile_image_url}
                          alt={selectedUser.name}
                          className="rounded-circle me-3"
                          style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                        />
                      ) : (
                        <i className="fas fa-user-circle fa-3x text-secondary me-3"></i>
                      )}
                      <div>
                        <h6 className="mb-0">{selectedUser.name}</h6>
                        <small className="text-muted">{selectedUser.email}</small>
                      </div>
                    </div>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setSelectedUser(null)}
                      disabled={submitting}
                    >
                      Change
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleInvite}
              disabled={!selectedUser || submitting}
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Sending...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane me-2"></i>
                  Send Invitation
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventOrganizersPage;
